import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402';

export interface BlogPost {
  id: string;
  creatorId: string;
  creatorName: string;
  creatorAvatar?: string;
  title: string;
  content: string;
  contentType: string;
  mediaUrls: string[];
  isPublic: boolean;
  isPinned: boolean;
  publishedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
  tags: string[];
  category: string | null;
  shareCount: number;
  likeCount: number;
  commentCount: number;
  viewCount: number;
}

export interface BlogPostListResponse {
  posts: BlogPost[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface BlogFilters {
  category?: string;
  tag?: string;
  creatorId?: string;
  search?: string;
  featured?: boolean;
}

class BlogService {
  /**
   * Get blog posts with pagination and filters
   */
  async getBlogPosts(
    page: number = 1,
    limit: number = 12,
    filters: BlogFilters = {}
  ): Promise<BlogPostListResponse> {
    const params: any = { page, limit, ...filters };
    const response = await axios.get(`${API_URL}/api/blog/posts`, { params });
    return response.data;
  }

  /**
   * Get a single blog post by ID
   */
  async getBlogPostById(postId: string): Promise<BlogPost> {
    const response = await axios.get(`${API_URL}/api/blog/posts/${postId}`);
    return response.data;
  }

  /**
   * Get featured blog posts
   */
  async getFeaturedPosts(limit: number = 3): Promise<BlogPost[]> {
    const response = await axios.get(`${API_URL}/api/blog/featured`, {
      params: { limit },
    });
    return response.data;
  }

  /**
   * Get all available categories
   */
  async getCategories(): Promise<string[]> {
    const response = await axios.get(`${API_URL}/api/blog/categories`);
    return response.data;
  }

  /**
   * Get popular tags
   */
  async getTags(limit: number = 20): Promise<{ tag: string; count: number }[]> {
    const response = await axios.get(`${API_URL}/api/blog/tags`, {
      params: { limit },
    });
    return response.data;
  }

  /**
   * Get related posts
   */
  async getRelatedPosts(postId: string, limit: number = 4): Promise<BlogPost[]> {
    const response = await axios.get(`${API_URL}/api/blog/posts/${postId}/related`, {
      params: { limit },
    });
    return response.data;
  }
}

export const blogService = new BlogService();
